import { useState, useEffect, useMemo } from 'react';
import { Palette } from 'lucide-react';
import { LoadingState } from '@/components/ui';
import { DesignUploadCard, PlacementModal } from '../components';
import { FRONT_PLACEMENTS, BACK_PLACEMENTS } from '../constants';
import type { WizardData, PlacementOption } from '../types';
import { uploadAsset } from '@/api/assets';
import {
  getCollectionPlacements,
  type PlacementPresetSummary,
} from '@/api/onboarding';

function presetToPlacementOption(
  preset: PlacementPresetSummary
): PlacementOption {
  return {
    id: `${preset.positionSlug}_${preset.presetSlug}`,
    label: preset.label,
    description: preset.description || '',
    icon: '📐',
  };
}

interface StepDesignProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}

/**
 * Step 4: Design Upload
 * Handles front and back design uploads with placement selection.
 * Loads placement presets from API based on selected collection.
 */
export function StepDesign({ data, onChange }: StepDesignProps) {
  const [frontModalOpen, setFrontModalOpen] = useState(false);
  const [backModalOpen, setBackModalOpen] = useState(false);

  // Placement presets from API
  const [apiPresets, setApiPresets] = useState<PlacementPresetSummary[] | null>(
    null
  );
  const [presetsLoading, setPresetsLoading] = useState(false);

  // Load presets from API when collection is selected
  useEffect(() => {
    if (!data.collectionId) return;

    let cancelled = false;
    setPresetsLoading(true);

    getCollectionPlacements(data.collectionId)
      .then((result) => {
        if (!cancelled) setApiPresets(result.presets);
      })
      .catch((err) => {
        console.warn('Failed to load placement presets, using defaults:', err);
        if (!cancelled) setApiPresets(null);
      })
      .finally(() => {
        if (!cancelled) setPresetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [data.collectionId]);

  const frontPlacements = useMemo<PlacementOption[]>(() => {
    if (!apiPresets) return FRONT_PLACEMENTS;
    const filtered = apiPresets.filter((p) => p.positionSlug === 'front');
    return filtered.length > 0
      ? filtered.map(presetToPlacementOption)
      : FRONT_PLACEMENTS;
  }, [apiPresets]);

  const backPlacements = useMemo<PlacementOption[]>(() => {
    if (!apiPresets) return BACK_PLACEMENTS;
    const filtered = apiPresets.filter((p) => p.positionSlug === 'back');
    return filtered.length > 0
      ? filtered.map(presetToPlacementOption)
      : BACK_PLACEMENTS;
  }, [apiPresets]);

  // Upload states
  const [frontUploading, setFrontUploading] = useState(false);
  const [frontProgress, setFrontProgress] = useState(0);
  const [frontError, setFrontError] = useState<string | null>(null);

  const [backUploading, setBackUploading] = useState(false);
  const [backProgress, setBackProgress] = useState(0);
  const [backError, setBackError] = useState<string | null>(null);

  const handleFrontFileSelect = async (file: File) => {
    // Store previous state in case upload fails
    const previousDesign = data.frontDesign;
    const previousPlacement = data.frontPlacement;

    setFrontError(null);
    setFrontUploading(true);
    setFrontProgress(0);

    // Clear design + placement at upload start
    onChange({ frontDesign: null, frontPlacement: '' });

    try {
      const result = await uploadAsset(file, {
        onProgress: setFrontProgress,
      });

      onChange({
        frontDesign: {
          assetId: result.id,
          assetUrl: result.url,
          fileName: file.name,
        },
      });

      // Open placement modal after successful upload
      setFrontModalOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setFrontError(message);
      // Restore previous design + placement
      if (previousDesign) {
        onChange({
          frontDesign: previousDesign,
          frontPlacement: previousPlacement,
        });
      }
    } finally {
      setFrontUploading(false);
    }
  };

  const handleBackFileSelect = async (file: File) => {
    // Store previous state in case upload fails
    const previousDesign = data.backDesign;
    const previousPlacement = data.backPlacement;

    setBackError(null);
    setBackUploading(true);
    setBackProgress(0);

    // Clear design + placement at upload start
    onChange({ backDesign: null, backPlacement: '' });

    try {
      const result = await uploadAsset(file, {
        onProgress: setBackProgress,
      });

      onChange({
        backDesign: {
          assetId: result.id,
          assetUrl: result.url,
          fileName: file.name,
        },
      });

      // Open placement modal after successful upload
      setBackModalOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setBackError(message);
      // Restore previous design + placement
      if (previousDesign) {
        onChange({
          backDesign: previousDesign,
          backPlacement: previousPlacement,
        });
      }
    } finally {
      setBackUploading(false);
    }
  };

  const handleRemoveFront = () => {
    onChange({ frontDesign: null, frontPlacement: '' });
    setFrontError(null);
  };

  const handleRemoveBack = () => {
    onChange({ backDesign: null, backPlacement: '' });
    setBackError(null);
  };

  const needsPlacementWarning =
    (data.frontDesign && !data.frontPlacement) ||
    (data.backDesign && !data.backPlacement);

  if (presetsLoading) {
    return <LoadingState label="Loading placement options..." />;
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-2">
        Upload your design and choose the position. You can upload front, back
        or both.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DesignUploadCard
          type="front"
          design={data.frontDesign}
          placement={data.frontPlacement}
          placements={frontPlacements}
          isUploading={frontUploading}
          uploadProgress={frontProgress}
          uploadError={frontError}
          onFileSelect={handleFrontFileSelect}
          onOpenPlacementModal={() => setFrontModalOpen(true)}
          onRemove={handleRemoveFront}
        />
        <DesignUploadCard
          type="back"
          design={data.backDesign}
          placement={data.backPlacement}
          placements={backPlacements}
          isUploading={backUploading}
          uploadProgress={backProgress}
          uploadError={backError}
          onFileSelect={handleBackFileSelect}
          onOpenPlacementModal={() => setBackModalOpen(true)}
          onRemove={handleRemoveBack}
        />
      </div>

      {needsPlacementWarning && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-control border border-warning bg-paper p-3"
        >
          <Palette
            className="h-4 w-4 shrink-0 text-warning"
            aria-hidden="true"
          />
          <p className="text-sm text-ink">
            Please choose a position for each uploaded design.
          </p>
        </div>
      )}

      <PlacementModal
        isOpen={frontModalOpen}
        onClose={() => setFrontModalOpen(false)}
        type="front"
        placements={frontPlacements}
        selectedPlacement={data.frontPlacement}
        onSelect={(placement) => onChange({ frontPlacement: placement })}
        fileName={data.frontDesign?.fileName || ''}
      />

      <PlacementModal
        isOpen={backModalOpen}
        onClose={() => setBackModalOpen(false)}
        type="back"
        placements={backPlacements}
        selectedPlacement={data.backPlacement}
        onSelect={(placement) => onChange({ backPlacement: placement })}
        fileName={data.backDesign?.fileName || ''}
      />
    </div>
  );
}
